/** Formulario multimodal de mercancías peligrosas (IMDG).
 *
 *  El documento que NO tiene datos propios: se construye sobre las MISMAS líneas
 *  del packing, leyendo otras columnas de la misma fila. Por eso este panel no
 *  consulta nada — recibe los datos ya corregidos del cajón y sólo cambia cómo
 *  los agrupa.
 *
 *  Y los agrupa por CLASIFICACIÓN, no por artículo, porque así se firma: una
 *  declaración de mercancía peligrosa se revisa por mercancía. Dos artículos con
 *  el mismo número ONU pero distinto grupo de embalaje son dos mercancías
 *  distintas en el documento.
 *
 *  Dos motivos por los que puede no proceder, y ninguno se arregla tecleando:
 *  una carga aérea (el IMDG es marítimo; lo aéreo va por IATA DGR y su documento
 *  es la DGD) y una carga sin ninguna línea con número ONU.
 */
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'

import { Loader } from '@/components/common/Loader'
import { documentosApi } from '@/services/api'
import type {
  DocumentoGenerado,
  MultimodalGrupo,
  PackingListCabeceraEditable,
  PackingListDatos,
} from '@/types'
import type { CorreccionEnviada } from '@/utils/packingList'
import {
  agruparMultimodal,
  articulosSinOnu,
  motivoSinMultimodal,
  totalesMultimodal,
} from '@/utils/multimodal'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'

import {
  Formulario,
  PieGenerar,
  Seccion,
  fmtEnt,
  fmtKg,
  mensajeDeError,
} from './piezas'

/** Los datos que este documento imprime y D365 no tiene.
 *
 *  Son ocho más que los del packing porque el multimodal es un formulario con
 *  casillas numeradas: la 2 (documento de transporte), la 5 (transitario), la 9
 *  (manipulación), las 11 y 12 (puertos) y las 20 y 22 (declarante). Ninguna
 *  existe en el ERP — se buscaron una a una entre los campos custom. */
const CAMPOS_MULTIMODAL = [
  { clave: 'fecha' as const, tipo: 'date' as const },
  { clave: 'referenciaExpedidor' as const },
  { clave: 'referenciaTransitario' as const },
  { clave: 'numeroDocumentoTransporte' as const },
  { clave: 'destinatarioNombre' as const },
  { clave: 'puertoCarga' as const },
  { clave: 'puertoDescarga' as const },
  { clave: 'navioVuelo' as const },
  { clave: 'infoManipulacion' as const },
  { clave: 'declaranteNombreCargo' as const },
  { clave: 'declaranteLugar' as const },
]

interface Props {
  dark: boolean
  empresa: string
  carga: string
  idioma: 'es' | 'en'
  /** Los datos del packing, ya con las correcciones aplicadas: es la misma
   *  fuente, así que un peso corregido allí llega aquí. */
  datos: PackingListDatos
  correcciones: CorreccionEnviada[]
  form: PackingListCabeceraEditable
  onCambioForm: (clave: keyof PackingListCabeceraEditable, valor: string) => void
  onGenerado: (pdf: DocumentoGenerado) => void
}

export function PanelMultimodal({
  dark, empresa, carga, idioma, datos, correcciones, form, onCambioForm, onGenerado,
}: Props) {
  const { t } = useTranslation()
  const [errorGen, setErrorGen] = useState<string | null>(null)

  const grupos = useMemo(() => agruparMultimodal(datos.lineas), [datos.lineas])
  const totales = useMemo(() => totalesMultimodal(grupos), [grupos])
  const fuera = useMemo(() => articulosSinOnu(datos.lineas), [datos.lineas])
  const motivo = motivoSinMultimodal(datos.lineas, datos.cabecera.esCargaAerea === 1)

  const generar = useMutation({
    mutationFn: () => documentosApi.generarMultimodal({
      empresa, carga, idioma, cabecera: form, origen: datos.origen, lineas: correcciones,
    }),
    onSuccess: r => { setErrorGen(null); onGenerado(r.data) },
    onError: (e: unknown) => {
      setErrorGen(mensajeDeError(e) ?? t('salesOrders.documentos.errorGenerar'))
    },
  })

  if (generar.isPending) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader label={t('salesOrders.documentos.generando')} />
      </div>
    )
  }

  // No es un dato que falte: es que el documento no procede. Se dice con todas
  // sus letras y sin ofrecer un botón que sólo daría un 422.
  if (motivo) {
    return (
      <div className="flex-1 flex items-center justify-center px-10">
        <div className="max-w-md text-center">
          <p className={`text-[12px] font-semibold ${dark ? 'text-slate-200' : 'text-slate-700'}`}>
            {t(`salesOrders.documentos.multimodal.${motivo}Titulo`)}
          </p>
          <p className={`text-[11px] mt-1.5 leading-relaxed ${
            dark ? 'text-slate-400' : 'text-slate-500'}`}>
            {t(`salesOrders.documentos.multimodal.${motivo}`, { empresa: empresa.toUpperCase() })}
          </p>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
        <TablaMercancias dark={dark} grupos={grupos} totales={totales} />
        {fuera.length > 0 && <FueraDelDocumento dark={dark} articulos={fuera} total={datos.lineas.length} />}
        <Formulario
          dark={dark}
          form={form}
          campos={CAMPOS_MULTIMODAL}
          onCambio={onCambioForm}
          titulo={t('salesOrders.documentos.multimodal.formTitle')}
          nota={t('salesOrders.documentos.multimodal.formNota')}
        />
      </div>

      <PieGenerar
        dark={dark}
        errorGen={errorGen}
        bloqueantes={0}
        listas={t('salesOrders.documentos.multimodal.mercanciasListas', { count: grupos.length })}
        numCorregidos={0}
        numExcluidas={0}
        onRestablecer={() => {}}
        puedeGenerar={grupos.length > 0}
        generar={t('salesOrders.documentos.multimodal.generar')}
        onGenerar={() => generar.mutate()}
      />
    </>
  )
}

// ── Tabla ────────────────────────────────────────────────────────────────────

function TablaMercancias({
  dark, grupos, totales,
}: {
  dark: boolean
  grupos: MultimodalGrupo[]
  totales: ReturnType<typeof totalesMultimodal>
}) {
  const { t } = useTranslation()
  const fmtArt = useFmtArticulo()

  const border = dark ? 'border-white/[0.07]' : 'border-slate-200'
  const divider = dark ? 'border-white/[0.05]' : 'border-slate-100'
  const th = `px-2 py-1.5 text-[9px] font-bold uppercase tracking-wider text-left ${
    dark ? 'text-slate-500' : 'text-slate-400'}`
  const td = `px-2 py-1.5 text-[11px] align-top ${dark ? 'text-slate-300' : 'text-slate-700'}`
  const totalCls = `px-2 py-1.5 text-[11px] font-bold ${dark ? 'text-slate-100' : 'text-slate-900'}`
  const mutedTxt = dark ? 'text-slate-400' : 'text-slate-500'

  return (
    <Seccion
      titulo={t('salesOrders.documentos.multimodal.title')}
      nota={t('salesOrders.documentos.multimodal.nota')}
      dark={dark}
    >
      <div className={`border rounded-lg overflow-x-auto ${border}`}>
        <table className="w-full border-collapse min-w-[820px]">
          <thead>
            <tr className={`border-b ${border}`}>
              <th className={th}>{t('salesOrders.documentos.multimodal.onu')}</th>
              <th className={th}>{t('salesOrders.documentos.multimodal.designacion')}</th>
              <th className={th}>{t('salesOrders.documentos.multimodal.clase')}</th>
              <th className={th}>{t('salesOrders.documentos.multimodal.grupoEmbalaje')}</th>
              <th className={th}>{t('salesOrders.documentos.multimodal.puntoInflamacion')}</th>
              <th className={`${th} text-right`}>{t('salesOrders.documentos.multimodal.bultos')}</th>
              <th className={`${th} text-right`}>{t('salesOrders.documentos.contenido.neto')}</th>
              <th className={`${th} text-right`}>{t('salesOrders.documentos.contenido.bruto')}</th>
              <th className={`${th} text-right`}>{t('salesOrders.documentos.multimodal.volumen')}</th>
            </tr>
          </thead>
          <tbody>
            {grupos.map((g, i) => (
              <tr key={`${g.onu}-${i}`} className={`border-t ${divider}`}>
                <td className={`${td} font-mono font-semibold`}>
                  {g.onu}
                  {g.onu.includes('/') && (
                    <span
                      title={t('salesOrders.documentos.avisos.onu_compuesto', { onu: g.onu })}
                      className={`ml-1 ${dark ? 'text-amber-400' : 'text-amber-600'}`}
                    >
                      *
                    </span>
                  )}
                </td>
                <td className={td}>
                  <div className="flex flex-col">
                    <span>{g.nombreExpedicion || '—'}</span>
                    {/* Qué referencias componen esta mercancía: sin esto, una
                        fila del documento no se puede contrastar con el packing. */}
                    <span className={`text-[10px] font-mono ${mutedTxt}`}>
                      {g.articulos.map(fmtArt).join(', ')}
                    </span>
                  </div>
                </td>
                <td className={td}>{g.clase || '—'}</td>
                <td className={td}>{g.grupoEmbalaje || '—'}</td>
                <td className={td}>{g.puntoInflamacion || '—'}</td>
                <td className={`${td} text-right tabular-nums`}>{fmtEnt(g.bultos)}</td>
                <td className={`${td} text-right tabular-nums`}>{fmtKg(g.pesoNeto)}</td>
                <td className={`${td} text-right tabular-nums`}>{fmtKg(g.pesoBruto)}</td>
                <td className={`${td} text-right tabular-nums`}>
                  {g.volumen ? g.volumen.toLocaleString('es-ES', { maximumFractionDigits: 3 }) : '—'}
                </td>
              </tr>
            ))}

            <tr className={`border-t-2 ${border} ${dark ? 'bg-white/[0.03]' : 'bg-slate-50'}`}>
              <td className={totalCls} colSpan={5}>
                {t('salesOrders.documentos.contenido.totales')}
              </td>
              <td className={`${totalCls} text-right tabular-nums`}>{fmtEnt(totales.bultos)}</td>
              <td className={`${totalCls} text-right tabular-nums`}>{fmtKg(totales.pesoNeto)}</td>
              <td className={`${totalCls} text-right tabular-nums`}>{fmtKg(totales.pesoBruto)}</td>
              <td className={`${totalCls} text-right tabular-nums`}>
                {totales.volumen.toLocaleString('es-ES', { maximumFractionDigits: 3 })}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p className={`text-[10px] mt-2 ${mutedTxt}`}>
        {t('salesOrders.documentos.multimodal.notaBruto')}
      </p>
    </Seccion>
  )
}

/** Lo que el multimodal deja fuera.
 *
 *  El documento declara menos referencias que el packing de la misma carga, y
 *  eso hay que verlo antes de firmarlo: si falta una que sí era peligrosa, el
 *  problema está en el maestro y se ve aquí, no en la aduana. */
function FueraDelDocumento({
  dark, articulos, total,
}: { dark: boolean; articulos: string[]; total: number }) {
  const { t } = useTranslation()
  const fmtArt = useFmtArticulo()
  return (
    <Seccion titulo={t('salesOrders.documentos.multimodal.fueraTitle')} dark={dark}>
      <div className={`rounded-lg border px-3 py-2 text-[11px] leading-relaxed ${
        dark ? 'bg-amber-500/10 border-amber-500/25 text-amber-300'
             : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
        <p>{t('salesOrders.documentos.multimodal.fuera', { count: articulos.length, total })}</p>
        <p className="mt-1 font-mono text-[10px] opacity-80">
          {articulos.map(fmtArt).join(', ')}
        </p>
      </div>
    </Seccion>
  )
}
